import React from 'react';
import tenant, { TENANT_IDS } from '../../config/tenant';


const KEYFRAMES = `
/* Sway: the fronds. Two groups, different periods, so the crown never pulses
   as one block. */
@keyframes es-sway    { 0%,100% { transform: rotate(-5deg); }  50% { transform: rotate(5deg); } }
@keyframes es-sway-2  { 0%,100% { transform: rotate(4deg); }   50% { transform: rotate(-6deg); } }

/* The trunk bends a little with them, or the tree looks like a cardboard cut-out. */
@keyframes es-bend    { 0%,100% { transform: rotate(-1.2deg); } 50% { transform: rotate(1.2deg); } }

/* A coconut lets go, falls, settles. Held off-stage most of the cycle so it
   reads as an occasional event rather than a rattle. */
@keyframes es-fall {
  0%, 55%   { transform: translateY(0) scale(1); opacity: 0; }
  57%       { opacity: .55; }
  78%       { transform: translateY(38px) scale(1); opacity: .55; }
  84%       { transform: translateY(34px) scaleY(.86) scaleX(1.1); opacity: .5; }
  90%       { transform: translateY(38px) scale(1); opacity: .4; }
  100%      { transform: translateY(38px) scale(1); opacity: 0; }
}
@keyframes es-bob     { 0%,100% { transform: translateY(0); }  50% { transform: translateY(-4px); } }

/* Crane: the jib slews, the hook hoists, the load swings a touch behind it. */
@keyframes es-jib     { 0%,100% { transform: rotate(-9deg); }  50% { transform: rotate(9deg); } }
@keyframes es-hoist   { 0%,100% { transform: translateY(0); }  50% { transform: translateY(16px); } }
@keyframes es-swing   { 0%,100% { transform: rotate(5deg); }   50% { transform: rotate(-5deg); } }
@keyframes es-blink   { 0%,49% { opacity: .9; } 50%,100% { opacity: .15; } }

@keyframes es-fade    { 0%,100% { opacity: .2; }               50% { opacity: .55; } }
@keyframes es-in      { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }

.es-root   { animation: es-in .45s ease-out both; }
.es-sway   { animation: es-sway 4.5s ease-in-out infinite;   transform-origin: 62px 40px; }
.es-sway-2 { animation: es-sway-2 6s ease-in-out infinite;   transform-origin: 62px 40px; }
.es-bend   { animation: es-bend 6s ease-in-out infinite;     transform-origin: 57px 88px; }
.es-fall   { animation: es-fall 7s cubic-bezier(.45,.05,.55,.95) infinite; }
.es-bob    { animation: es-bob 3.5s ease-in-out infinite; }

.es-jib    { animation: es-jib 6s ease-in-out infinite;       transform-origin: 62px 26px; }
.es-hoist  { animation: es-hoist 3.2s ease-in-out infinite; }
.es-swing  { animation: es-swing 3.2s ease-in-out infinite;   transform-origin: 92px 26px; }
.es-blink  { animation: es-blink 1.6s steps(1,end) infinite; }
.es-fade   { animation: es-fade 5s ease-in-out infinite; }

@media (prefers-reduced-motion: reduce) {
  .es-root, .es-sway, .es-sway-2, .es-bend, .es-fall, .es-bob,
  .es-jib, .es-hoist, .es-swing, .es-blink, .es-fade {
    animation: none !important;
  }
}
`;

/* ── COCO — a coconut palm, fronds moving in the breeze ──────────────────── */
function CocoScene() {
  return (
    <svg viewBox="0 0 120 100" className="w-32 h-28" fill="none" aria-hidden="true">
      <ellipse cx="60" cy="88" rx="34" ry="4" fill="currentColor" opacity=".08" />

      {/* The trunk and crown bend as one, so the tree sways from its base. */}
      <g className="es-bend">
        <path d="M57 88 C57 72 55 60 62 40" stroke="currentColor" strokeWidth="4"
              strokeLinecap="round" opacity=".55" />
        {[78, 70, 62, 54, 46].map((y, i) => (
          <path key={y} d={`M${56.4 + i * 0.9} ${y} l4 -1.6`} stroke="currentColor"
                strokeWidth="1.2" strokeLinecap="round" opacity=".3" />
        ))}

        <g className="es-sway" opacity=".7">
          <path d="M62 40 C48 33 38 34 30 40" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
          <path d="M62 40 C50 28 40 24 32 24" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
        </g>
        <g className="es-sway-2" opacity=".7">
          <path d="M62 40 C76 33 86 34 94 41" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
          <path d="M62 40 C74 28 84 25 92 26" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
          <path d="M62 40 C66 30 66 24 64 18" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
        </g>

        <g className="es-bob">
          <circle cx="58" cy="45" r="3.2" fill="currentColor" opacity=".5" />
          <circle cx="65" cy="46" r="2.6" fill="currentColor" opacity=".38" />
        </g>

        {/* One lets go every seven seconds, drops, and settles on the ground. */}
        <circle className="es-fall" cx="63" cy="47" r="2.8" fill="currentColor" />
      </g>

      <circle cx="82" cy="85" r="3" fill="currentColor" opacity=".2" />
    </svg>
  );

}

/* ── DGE — a tower crane, jib swinging, hook rising and falling ──────────── */
function ConstructionScene() {
  return (
    <svg viewBox="0 0 120 100" className="w-32 h-28" fill="none" aria-hidden="true">
      <ellipse cx="60" cy="88" rx="34" ry="4" fill="currentColor" opacity=".08" />

      <path d="M56 88 L56 28 M68 88 L68 28" stroke="currentColor" strokeWidth="2.4"
            strokeLinecap="round" opacity=".55" />
      {[34, 44, 54, 64, 74, 84].map((y, i) => (
        <path key={y} d={i % 2 ? `M56 ${y} L68 ${y - 10}` : `M56 ${y - 10} L68 ${y}`}
              stroke="currentColor" strokeWidth="1.1" opacity=".3" />
      ))}

      {/* The whole jib slews about the mast head. */}
      <g className="es-jib">
        <path d="M28 26 L104 26" stroke="currentColor" strokeWidth="2.4"
              strokeLinecap="round" opacity=".55" />
        <path d="M62 16 L34 26 M62 16 L98 26" stroke="currentColor" strokeWidth="1.4" opacity=".35" />
        <rect x="28" y="22" width="9" height="8" rx="1.5" fill="currentColor" opacity=".35" />
        <path d="M62 26 L62 16" stroke="currentColor" strokeWidth="1.6" opacity=".4" />
        {/* warning light on the mast head */}
        <circle className="es-blink" cx="62" cy="14" r="1.6" fill="currentColor" />

        {/* Hook and load: hoisting up and down, the load swinging slightly
            behind the movement the way a real one lags its crane. */}
        <g className="es-hoist">
          <g className="es-swing">
            <path d="M92 26 L92 44" stroke="currentColor" strokeWidth="1.2" opacity=".45" />
            <path d="M88 44 L96 44 L92 50 Z" fill="currentColor" opacity=".4" />
            <rect x="85" y="50" width="14" height="7" rx="1.5" fill="currentColor" opacity=".33" />
          </g>
        </g>
      </g>

      <g className="es-fade">
        <rect x="34" y="80" width="16" height="5" rx="1" fill="currentColor" />
        <rect x="37" y="75" width="10" height="5" rx="1" fill="currentColor" />
      </g>
    </svg>
  );

}

const isCoco = () => tenant?.id === TENANT_IDS.COCO;


export default function EmptyState({ title, message, action = null, compact = false }) {
  return (
    <div className={`es-root flex flex-col items-center justify-center text-center
                     ${compact ? 'py-8' : 'py-14'} px-6`}>
      <style>{KEYFRAMES}</style>

      <div className={`text-primary-500 ${compact ? 'scale-90' : ''}`}>
        {isCoco() ? <CocoScene /> : <ConstructionScene />}
      </div>

      <h4 className="mt-3 text-[13px] font-semibold text-slate-700">{title}</h4>
      {message && (
        <p className="mt-1 text-[11.5px] text-slate-400 max-w-[300px] leading-relaxed">
          {message}
        </p>
      )}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}